/**
 * Jelvények — katalógus és kiértékelés.
 *
 * docs/04-pontrendszer.md → Jelvények.
 *
 * A KATALÓGUS KÓDBAN VAN, nem csak Firestore-ban, jóllehet a spec „adatvezérelt,
 * `badges` kollekció"-t ír. Az admin CRUD-szerkesztő (docs/06 → 8. Jelvény- és
 * szintkatalógus) még nincs megírva — amíg nincs felület, ami módosítaná, a
 * kódban tartott katalógus egyszerűbb és típusbiztos, ugyanúgy, ahogy a
 * `GAMEPLAY.LEVEL_NAMES` is kódban él. A `badges/{id}` Firestore-dokumentumok
 * erről a katalógusról vannak vetítve (lásd `server/src/scripts/seedBadges.ts`),
 * hogy a séma a jövőbeli admin-szerkesztéshez és a kihívások
 * `rewardBadgeId` hivatkozásaihoz is készen álljon.
 *
 * MIÉRT NEM MINDEN KATEGÓRIA VAN ITT? A specben tíz kategória van, ebben a
 * körben HAT készült el — csak azok, amikhez MA VAN valódi adat:
 *
 *   - Első lépések (3/4): „első visszaszerzés" kimaradt, mert a
 *     `territoryEvents` ma nem különbözteti meg a lopást a visszaszerzéstől
 *     (ugyanaz a `territory_stolen` esemény) — ez játékegyensúly-kérdés,
 *     „Amit kérdezz meg, ne találj ki" (AGENTS.md).
 *   - Védő, Felfedező, Közösség, Pro: KIMARADT, mert az alattuk lévő adat
 *     nincs megírva (nincs védekezés-eseménynapló, a `passport` sosem
 *     íródik, nincs klub/kihívás-tagság, az előfizetés-életciklus nincs
 *     megépítve). Ezeket kitalálni hazugság lenne a felhasználónak.
 */

import { GAMEPLAY } from '@/config/gameplay';

export type BadgeTier = 'bronze' | 'silver' | 'gold' | 'platinum';
export type BadgeCategory =
  | 'first_steps'
  | 'distance'
  | 'territory'
  | 'conqueror'
  | 'streak'
  | 'week_streak'
  | 'loyalty'
  | 'explorer'
  | 'social';

/**
 * A HALMOZHATÓ jelvények családjai. Egy létra ugyanannak a teljesítménynek
 * az egyre magasabb fokai (10, 50, 100 km …), ezért a felhasználó ezeket
 * EGY dologként éli meg — a profilon egyetlen lapozható kártyán látszanak,
 * nem húsz külön címkéként.
 *
 * Az egyedi jelvényeknek (`first_steps`) nincs létrájuk: azok egyszeri,
 * megismételhetetlen pillanatok.
 */
export type BadgeLadder =
  | 'distance_foot'
  | 'distance_bike'
  | 'territory'
  | 'conqueror'
  | 'streak'
  | 'week_streak'
  | 'loyalty'
  | 'explorer'
  | 'comments'
  | 'likes_received'
  | 'likes_given';

export interface BadgeDef {
  id: string;
  category: BadgeCategory;
  name: string;
  /**
   * A létra-fok ÖNMAGÁBAN, a családnév nélkül — például „50 km".
   *
   * A lapozható jelvénykártyán a család neve a szalag fejlécében áll, a
   * kártyákon pedig csak a fok — enélkül tízszer egymás mellett állna,
   * hogy „Bringás táv". Szándékosan ITT van, a név mellett, nem a
   * felületen egy kötőjel mentén kettévágva.
   */
  shortName: string;
  description: string;
  tier: BadgeTier;
  rewardGp: number;
  /** `null`: egyedi jelvény, nem része létrának. */
  ladder: BadgeLadder | null;
  /** Hányadik fok a létrán (0-tól). Egyedi jelvénynél mindig 0. */
  ladderIndex: number;
  /** Hány fokból áll a létra. Egyedi jelvénynél mindig 1. */
  ladderSize: number;
}

/** A kiértékeléshez szükséges, MÁR ISMERT állapot — mindig a friss profilból jön. */
export interface BadgeContext {
  activitiesCount: number;
  distanceKm: { run: number; walk: number; ride: number };
  /** Összesen elfoglalt terület, mindkét réteg összegezve, m²-ben. */
  territoryM2Total: number;
  /** Hányszor indított sikeres területrablást — `territoryEvents` darabszám. */
  stealCount: number;
  /** A valaha elért leghosszabb napi sorozat (`streak.longest`, sosem csökken). */
  streakLongestDays: number;
  /** A valaha elért heti mérföldkövek (`streak.milestonesAwarded`, sosem törlődik). */
  weekMilestonesAwarded: readonly number[];
  /** A fiók kora napokban (`Date.now() - createdAt`). */
  accountAgeDays: number;
  /**
   * A MOSTANI heti sorozat (`streak.weeks`) — csak a haladás kijelzéséhez
   * (`ladderProgress`); a jelvény maga a kiosztott mérföldkőből jön.
   */
  weekStreakWeeks?: number;

  /* ── Az „első lépések" második köre ────────────────────────────────
     Mindegyik MÉRT adatból jön; lásd `server/src/lib/badges.ts` →
     `buildContext`. Ami nincs megmérve, az nem is kerülhetett ide. */

  /** Van-e feltöltött profilkép. */
  hasPhoto: boolean;
  /** Van-e legalább egy regisztrált push-eszköz (`devices/{uid}/tokens`). */
  hasPushDevice: boolean;
  /** Hány bandát ALAPÍTOTT (`role === 'owner'`). */
  bandasFounded: number;
  /** Hány MÁSÉBA lépett be (`role !== 'owner'`). */
  bandasJoined: number;
  /** Hány riválisa van (`users/{uid}/rivals`). */
  rivalCount: number;
  /** Hány aktivitást indított TERVEZETT ÚTVONALBÓL. */
  routeStartedCount: number;
  /** Hány aktivitást indított KÜLDETÉSBŐL. */
  missionStartedCount: number;
  /** Hány hozzászólást írt. */
  commentCount: number;
  /** Hány szívet KAPOTT az aktivitásaira. */
  likesReceived: number;
  /** Hány szívet ADOTT másoknak. */
  likesGiven: number;
  /**
   * Hány ország van az útlevélben (`users/{uid}/passport`, DECISIONS #101).
   * Opcionális: a régi hívók és tesztek nélküle 0-t jelentenek.
   */
  countriesVisited?: number;
}

function badge(
  id: string,
  category: BadgeCategory,
  name: string,
  description: string,
  tier: BadgeTier,
  rewardGp: number,
  ladder: BadgeLadder | null = null,
  ladderIndex = 0,
  ladderSize = 1,
  shortName = name,
): BadgeDef {
  return {
    id,
    category,
    name,
    shortName,
    description,
    tier,
    rewardGp,
    ladder,
    ladderIndex,
    ladderSize,
  };
}

/**
 * A négy fokozat egy létrán belül egyenletesen oszlik el.
 *
 * Az utolsó fokozat mindig platina — az a legritkább, legrangosabb elem egy
 * létrán. A többi a hossztól függően bronz→ezüst→arany sorrendben tölt fel.
 */
function tierFor(index: number, total: number): BadgeTier {
  if (index === total - 1) return 'platinum';
  const ratio = index / Math.max(1, total - 1);
  if (ratio < 0.34) return 'bronze';
  if (ratio < 0.67) return 'silver';
  return 'gold';
}

/* ── Első lépések ────────────────────────────────────────────────── */

const FIRST_STEPS: BadgeDef[] = [
  badge('first_activity', 'first_steps', 'Első lépés', 'Rögzítetted az első aktivitásodat.', 'bronze', 20),
  badge(
    'first_loop',
    'first_steps',
    'Első bezárt kör',
    'Bezártál egy kört, és terület lett a tiéd.',
    'bronze',
    50,
  ),
  badge(
    'first_steal',
    'first_steps',
    'Első hódítás',
    'Elvettél egy darab területet egy másik játékostól.',
    'silver',
    80,
  ),
  /*
    A MÁSODIK KÖR — Geri kérése (2026-09-16). Mindegyik MÉRT adatból jön;
    a nevek szándékosan játékosak, mert ezek nem teljesítmény-fokozatok,
    hanem egyszeri pillanatok a beszokásból.
  */
  badge(
    'first_photo',
    'first_steps',
    'Hi there!',
    'Feltöltötted az első profilképedet.',
    'bronze',
    20,
  ),
  badge(
    'first_push',
    'first_steps',
    'FOMO',
    'Bekapcsoltad a PUSH értesítéseket.',
    'bronze',
    20,
  ),
  badge(
    'first_banda_owner',
    'first_steps',
    'Finucci',
    'Alapítottál egy bandát.',
    'silver',
    100,
  ),
  badge(
    'first_banda_member',
    'first_steps',
    'Toretto',
    'Beléptél valaki bandájába.',
    'bronze',
    60,
  ),
  badge(
    'first_rival',
    'first_steps',
    'PvP',
    'Megvan az első riválisod.',
    'bronze',
    50,
  ),
  badge(
    'first_route',
    'first_steps',
    'Forrest',
    'Tervezett útvonalból indítottad az első rögzítést.',
    'bronze',
    50,
  ),
  badge(
    'first_mission',
    'first_steps',
    'Top Gun',
    'Küldetésből indítottad az első rögzítést.',
    'bronze',
    50,
  ),
];

/* ── Távolság (rétegenként) ──────────────────────────────────────── */

/* Az 50 000 km fölötti, csak bringás fokok jutalma (2026-11-02) a 10–25 000 km
   közötti ×1,5-es ütemet követi — javaslat, Geri nem adott értéket. */
const DISTANCE_REWARDS: readonly number[] = [
  50, 150, 300, 600, 1000, 1800, 3000, 5000, 8000, 14000,
  21000, 32000, 48000, 72000, 108000,
];

const FOOT_DISTANCE_LADDER_KM: readonly number[] = GAMEPLAY.DISTANCE_BADGE_LADDER_KM;
const BIKE_DISTANCE_LADDER_KM: readonly number[] = [
  ...GAMEPLAY.DISTANCE_BADGE_LADDER_KM,
  ...GAMEPLAY.DISTANCE_BIKE_BADGE_EXTRA_KM,
];

/**
 * Ezres tagolás a katalógus magyar alapszövegéhez: „1 000 000".
 *
 * A felület a `src/lib/format.ts`-en át formáz (`badgeText`); ez csak a
 * szerver által is olvasott alapszöveg (értesítés, `badges/{id}`). A
 * választó ugyanaz a nem törő szóköz (U+00A0), amit a hu-HU `Intl` ad —
 * a `badgeText` tesztje a kettő egyezését ellenőrzi.
 */
function groupThousands(value: number): string {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
}

function distanceBadges(layer: 'foot' | 'bike', label: string): BadgeDef[] {
  const ladder = layer === 'foot' ? FOOT_DISTANCE_LADDER_KM : BIKE_DISTANCE_LADDER_KM;
  return ladder.map((km, index) =>
    badge(
      `distance_${layer}_${km}`,
      'distance',
      `${label} — ${groupThousands(km)} km`,
      `Összesen ${groupThousands(km)} km-t tettél meg ${label.toLowerCase()}.`,
      tierFor(index, ladder.length),
      DISTANCE_REWARDS[index] ?? 100,
      layer === 'foot' ? 'distance_foot' : 'distance_bike',
      index,
      ladder.length,
      `${groupThousands(km)} km`,
    ),
  );
}

const DISTANCE = [...distanceBadges('foot', 'Gyalogos táv'), ...distanceBadges('bike', 'Bringás táv')];

/* ── Terület ─────────────────────────────────────────────────────── */

const TERRITORY_LADDER_M2: readonly number[] = [
  100_000, 500_000, 1_000_000, 5_000_000, 25_000_000, 50_000_000,
  100_000_000, 250_000_000, 500_000_000, 1_000_000_000,
  /* Geri, 2026-11-02: 2 500 – 100 000 km². */
  2_500_000_000, 5_000_000_000, 10_000_000_000, 20_000_000_000,
  50_000_000_000, 100_000_000_000,
];
/* A 100–1000 km² fokok jutalma (#70) a korábbi ×1,5-es ütemet követi — javaslat,
   Geri nem adott értéket; a GP-egyensúly miatt itt hangolható. */
/* A 2 500 – 100 000 km² fokok jutalma (2026-11-02) ugyanezt a ×1,5-öt viszi
   tovább — szintén javaslat. */
const TERRITORY_REWARDS: readonly number[] = [
  80, 250, 500, 1500, 4000, 8000, 12000, 18000, 27000, 40000,
  60000, 90000, 135000, 200000, 300000, 450000,
];

const TERRITORY: BadgeDef[] = TERRITORY_LADDER_M2.map((m2, index) =>
  badge(
    `territory_${m2}`,
    'territory',
    `Birodalom — ${formatKm2(m2)} km²`,
    `Összesen ${formatKm2(m2)} km² terület a tiéd, valaha egyszerre birtokolva.`,
    tierFor(index, TERRITORY_LADDER_M2.length),
    TERRITORY_REWARDS[index] ?? 100,
    'territory',
    index,
    TERRITORY_LADDER_M2.length,
    `${formatKm2(m2)} km²`,
  ),
);

function formatKm2(m2: number): string {
  const km2 = m2 / 1_000_000;
  /* Magyar tizedesvessző — a `toFixed` pontot adna („0.1 km²", GRUNDO #61). */
  return km2 >= 1 ? groupThousands(km2) : km2.toFixed(1).replace('.', ',');
}

/* ── Hódító ──────────────────────────────────────────────────────── */

const CONQUEROR_LADDER: readonly number[] = [10, 50, 100, 500];
const CONQUEROR_REWARDS: readonly number[] = [100, 400, 900, 3000];

const CONQUEROR: BadgeDef[] = CONQUEROR_LADDER.map((count, index) =>
  badge(
    `conqueror_${count}`,
    'conqueror',
    `Hódító — ${count}×`,
    `${count} sikeres területrablás áll mögötted.`,
    tierFor(index, CONQUEROR_LADDER.length),
    CONQUEROR_REWARDS[index] ?? 100,
    'conqueror',
    index,
    CONQUEROR_LADDER.length,
    `${count}×`,
  ),
);

/* ── Kitartás — napi sorozat ─────────────────────────────────────── */

const STREAK_LADDER_DAYS: readonly number[] = [7, 30, 100, 365];
const STREAK_REWARDS: readonly number[] = [50, 300, 1200, 6000];

const STREAK: BadgeDef[] = STREAK_LADDER_DAYS.map((days, index) =>
  badge(
    `streak_${days}`,
    'streak',
    `${days} napos sorozat`,
    `${days} egymást követő napon voltál aktív.`,
    tierFor(index, STREAK_LADDER_DAYS.length),
    STREAK_REWARDS[index] ?? 100,
    'streak',
    index,
    STREAK_LADDER_DAYS.length,
    `${days} nap`,
  ),
);

/* ── Kitartás — heti sorozat ─────────────────────────────────────── */

/**
 * A KÜSZÖBÖK MEGEGYEZNEK a `GAMEPLAY.WEEK_STREAK_MILESTONES` kulcsaival.
 *
 * Ez nem véletlen: a heti mérföldkő GP-jutalma már létezik és működik
 * (`dailyRollover.ts` → `milestonesAwarded`), a jelvény csak egy MÁSODIK,
 * vizuális elismerést ad ugyanahhoz a pillanathoz — nem talál ki új
 * küszöböt.
 */
const WEEK_STREAK_LADDER: readonly number[] = Object.keys(GAMEPLAY.WEEK_STREAK_MILESTONES)
  .map(Number)
  .sort((a, b) => a - b);
const WEEK_STREAK_REWARDS: readonly number[] = [50, 200, 500, 1500];

const WEEK_STREAK: BadgeDef[] = WEEK_STREAK_LADDER.map((weeks, index) =>
  badge(
    `week_streak_${weeks}`,
    'week_streak',
    `${weeks} hetes sorozat`,
    `${weeks} egymást követő héten voltál aktív.`,
    tierFor(index, WEEK_STREAK_LADDER.length),
    WEEK_STREAK_REWARDS[index] ?? 100,
    'week_streak',
    index,
    WEEK_STREAK_LADDER.length,
    `${weeks} hét`,
  ),
);

/* ── Hűség ───────────────────────────────────────────────────────── */

/* A 3–20 éves fokok: Geri, 2026-11-02. Egy év 365 nap, mint a 2 évesnél. */
const LOYALTY_LADDER_DAYS: readonly number[] = [30, 182, 365, 730, 1095, 1460, 1825, 3650, 5475, 7300];
const LOYALTY_NAMES: readonly string[] = [
  '1 hónap', '6 hónap', '1 év', '2 év', '3 év', '4 év', '5 év', '10 év', '15 év', '20 év',
];
/* A 3 évtől a jutalom javaslat (Geri nem adott értéket), évente +500 GP-vel nő. */
const LOYALTY_REWARDS: readonly number[] = [30, 150, 400, 1000, 1500, 2000, 2500, 5000, 7500, 10000];

const LOYALTY: BadgeDef[] = LOYALTY_LADDER_DAYS.map((days, index) =>
  badge(
    `loyalty_${days}`,
    'loyalty',
    `${LOYALTY_NAMES[index]} a GRUNDO-ban`,
    `${LOYALTY_NAMES[index]} telt el a csatlakozásod óta.`,
    tierFor(index, LOYALTY_LADDER_DAYS.length),
    LOYALTY_REWARDS[index] ?? 50,
    'loyalty',
    index,
    LOYALTY_LADDER_DAYS.length,
    LOYALTY_NAMES[index] ?? `${days} nap`,
  ),
);

/* ── Felfedező — országok az útlevélben (DECISIONS #101) ─────────── */

/** Szintek és jutalom: Geri döntése (2026-10-15) — felülírja a docs/04 5 / 25 / 50-ét. */
const EXPLORER_LADDER: readonly number[] = [1, 3, 5, 10, 25, 50, 100];
const EXPLORER_REWARDS: readonly number[] = [100, 500, 1000, 2000, 4000, 10000, 50000];

const EXPLORER: BadgeDef[] = EXPLORER_LADDER.map((count, index) =>
  badge(
    `explorer_${count}`,
    'explorer',
    `Felfedező — ${count} ország`,
    `${count} ország van az útleveledben.`,
    tierFor(index, EXPLORER_LADDER.length),
    EXPLORER_REWARDS[index] ?? 100,
    'explorer',
    index,
    EXPLORER_LADDER.length,
    `${count} ország`,
  ),
);

/* ── Közösség — hozzászólás és szívecske ─────────────────────────── */

/**
 * A HÁROM KÖZÖSSÉGI LÉTRA UGYANARRA A FOKOZÁSRA MEGY.
 *
 * Egy hozzászólás és egy szívecske nagyjából ugyanakkora gesztus, ezért
 * nincs értelme külön skálát kitalálni nekik — a közös létra egyben azt is
 * jelenti, hogy egy fokot ugyanott ér el a szószátyár és a szívosztogató.
 *
 * ⚠️ A SZÁMLÁLÓK NULLÁRÓL INDULNAK. A hozzászólásokat és a szíveket eddig
 * senki nem összegezte felhasználóra (`activities/{id}/comments`, illetve
 * `.../likes` alkollekciók), visszamenőleg pedig nem számoljuk újra: az
 * több százezer dokumentum végigolvasása lenne egyetlen jelvényért. Aki ma
 * regisztrál és aki tavaly, innentől ugyanonnan indul — ezt tudni kell.
 */
const SOCIAL_LADDER: readonly number[] = [1, 10, 50, 100, 250, 500, 1000];
const SOCIAL_REWARDS: readonly number[] = [20, 60, 150, 300, 700, 1500, 3000];

function socialBadges(
  ladder: 'comments' | 'likes_received' | 'likes_given',
  label: string,
  unit: string,
  describe: (count: number) => string,
): BadgeDef[] {
  return SOCIAL_LADDER.map((count, index) =>
    badge(
      `${ladder}_${count}`,
      'social',
      `${label} — ${count} ${unit}`,
      describe(count),
      tierFor(index, SOCIAL_LADDER.length),
      SOCIAL_REWARDS[index] ?? 100,
      ladder,
      index,
      SOCIAL_LADDER.length,
      `${count} ${unit}`,
    ),
  );
}

const SOCIAL: BadgeDef[] = [
  ...socialBadges(
    'comments',
    'Nagy dumás',
    'komment',
    (count) => `${count} hozzászólást írtál mások aktivitásaihoz.`,
  ),
  ...socialBadges(
    'likes_received',
    'LOVE',
    'szív',
    (count) => `${count} szívecskét kaptál az aktivitásaidra.`,
  ),
  ...socialBadges(
    'likes_given',
    'Cupido',
    'szív',
    (count) => `${count} szívecskét adtál mások aktivitásaira.`,
  ),
];

/* ── A teljes katalógus ──────────────────────────────────────────── */

export const BADGES: readonly BadgeDef[] = [
  ...FIRST_STEPS,
  ...DISTANCE,
  ...TERRITORY,
  ...CONQUEROR,
  ...STREAK,
  ...WEEK_STREAK,
  ...LOYALTY,
  ...EXPLORER,
  ...SOCIAL,
];

export const BADGES_BY_ID: ReadonlyMap<string, BadgeDef> = new Map(
  BADGES.map((entry) => [entry.id, entry]),
);

/**
 * A létrák, fokonként növekvő sorrendben — a lapozható jelvénykártyákhoz.
 *
 * A sorrend a `ladderIndex`, nem a `BADGES` tömbbeli helyzet: az utóbbi ma
 * ugyan egybeesik, de egy jövőbeli beszúrás csendben összekeverné a
 * filmszalagot.
 */
export const BADGE_LADDERS: ReadonlyMap<BadgeLadder, readonly BadgeDef[]> = (() => {
  const map = new Map<BadgeLadder, BadgeDef[]>();
  for (const entry of BADGES) {
    if (!entry.ladder) continue;
    const list = map.get(entry.ladder) ?? [];
    list.push(entry);
    map.set(entry.ladder, list);
  }
  for (const list of map.values()) list.sort((a, b) => a.ladderIndex - b.ladderIndex);
  return map;
})();

/** A jelvények nem létrához tartozó, egyszeri darabjai. */
export const UNIQUE_BADGES: readonly BadgeDef[] = BADGES.filter((entry) => entry.ladder === null);

/* ── Rangfokozat ─────────────────────────────────────────────────── */

/**
 * A létra fokainak SZÍNSORRENDJE — Geri döntése (2026-09-16).
 *
 * Az első öt a klasszikus érem-sor (bronz → gyémánt), fölötte
 * drágakövek folytatják. A tizedik fok fölött (Birodalom, bringás táv,
 * Hűség — 2026-11-02) az obszidián ismétlődik; a fokot ott a hatszögek
 * száma különbözteti meg.
 *
 * ⚠️ A SORREND MINDENHOL UGYANEZ, a létra hosszától függetlenül. Egy
 * négyfokú létra tehát bronz-ezüst-arany-platina, nem pedig arányosan
 * széthintve a teljes skálán — korábban az volt, és emiatt egy-egy létra
 * átugrotta az aranyat.
 */
export type BadgeTone =
  | 'bronze'
  | 'silver'
  | 'gold'
  | 'platinum'
  | 'diamond'
  | 'emerald'
  | 'ruby'
  | 'sapphire'
  | 'amethyst'
  | 'obsidian';

export const BADGE_TONES: readonly BadgeTone[] = [
  'bronze',
  'silver',
  'gold',
  'platinum',
  'diamond',
  'emerald',
  'ruby',
  'sapphire',
  'amethyst',
  'obsidian',
];

export interface BadgeRank {
  /**
   * Hányadik fok a létrán, 1-től — ÉS ENNYI HATSZÖG kerül a
   * rangjelzésre. Egyedi (nem létrás) jelvénynél mindig 1.
   */
  position: number;
  tone: BadgeTone;
}

/**
 * A kártyán látszó RANGFOKOZAT.
 *
 * ⚠️ EZ NEM A `tier` MEZŐ, ÉS NEM IS VÁLTJA FEL. A `tier` a spec négyfokú
 * ritkasága (docs/04 → Jelvények), ami a `badges/{id}` Firestore-séma része
 * és a GP-jutalomhoz tartozik — azt nem írjuk át. Ez ugyanannak a
 * mennyiségnek (a létrán elfoglalt helynek) a TELJES felbontású változata:
 * minden fok saját színt és saját hatszögszámot kap. Ezért nem mondhatnak
 * ellent egymásnak.
 *
 * A kártya SZÁNDÉKOSAN csak ezt mutatja, a `tier`-t nem — két, egymáshoz
 * közeli, de eltérő felbontású jelölés egy lapon összezavarna.
 */
export function badgeRank(def: BadgeDef): BadgeRank {
  const position = def.ladderIndex + 1;
  /* A tíznél hosszabb létrán a legfelső szín ismétlődik, ahelyett hogy
     `undefined` dőlne ki a CSS-be. */
  const tone = BADGE_TONES[Math.min(position, BADGE_TONES.length) - 1] ?? 'bronze';
  return { position, tone };
}

/**
 * Melyik jelvényeket érdemli ki a felhasználó a mostani állapotában.
 *
 * TISZTA FÜGGVÉNY: csak az `ctx`-ből számol, Firestore-t nem lát. A hívó dolga
 * eldönteni, melyik közülük ÚJ (diffelve a már meglévő
 * `users/{uid}/badges/{id}` dokumentumokkal) — ez a függvény szándékosan nem
 * tud a korábbi állapotról, mert egy létra-küszöb elérése MINDIG ugyanazt az
 * eredményt kell adja, függetlenül attól, mikor futtatjuk. Ez teszi
 * biztonságossá az újrafuttatást (aktivitás után ÉS a napi fordulóban is),
 * és ez teszi lehetővé egy jövőbeli visszamenőleges kiosztást is —
 * ugyanezzel a függvénnyel.
 */
export function earnedBadgeIds(ctx: BadgeContext): string[] {
  const ids: string[] = [];

  if (ctx.activitiesCount >= 1) ids.push('first_activity');
  // A cellabirtoklás CSAK bezárt körből származhat (03-jatekszabalyok.md →
  // „A bezárás = önmetszés"), tehát megbízható helyettesítő a „volt-e már
  // bezárt köröd" kérdésre — nem kell külön eseménynaplózás hozzá.
  if (ctx.territoryM2Total > 0) ids.push('first_loop');
  if (ctx.stealCount >= 1) ids.push('first_steal');
  if (ctx.hasPhoto) ids.push('first_photo');
  if (ctx.hasPushDevice) ids.push('first_push');
  if (ctx.bandasFounded >= 1) ids.push('first_banda_owner');
  if (ctx.bandasJoined >= 1) ids.push('first_banda_member');
  if (ctx.rivalCount >= 1) ids.push('first_rival');
  if (ctx.routeStartedCount >= 1) ids.push('first_route');
  if (ctx.missionStartedCount >= 1) ids.push('first_mission');

  const foot = ctx.distanceKm.run + ctx.distanceKm.walk;
  for (const km of FOOT_DISTANCE_LADDER_KM) {
    if (foot >= km) ids.push(`distance_foot_${km}`);
  }
  for (const km of BIKE_DISTANCE_LADDER_KM) {
    if (ctx.distanceKm.ride >= km) ids.push(`distance_bike_${km}`);
  }

  for (const m2 of TERRITORY_LADDER_M2) {
    if (ctx.territoryM2Total >= m2) ids.push(`territory_${m2}`);
  }

  for (const count of CONQUEROR_LADDER) {
    if (ctx.stealCount >= count) ids.push(`conqueror_${count}`);
  }

  for (const days of STREAK_LADDER_DAYS) {
    if (ctx.streakLongestDays >= days) ids.push(`streak_${days}`);
  }

  for (const weeks of WEEK_STREAK_LADDER) {
    if (ctx.weekMilestonesAwarded.includes(weeks)) ids.push(`week_streak_${weeks}`);
  }

  for (const days of LOYALTY_LADDER_DAYS) {
    if (ctx.accountAgeDays >= days) ids.push(`loyalty_${days}`);
  }

  for (const count of EXPLORER_LADDER) {
    if ((ctx.countriesVisited ?? 0) >= count) ids.push(`explorer_${count}`);
  }

  for (const count of SOCIAL_LADDER) {
    if (ctx.commentCount >= count) ids.push(`comments_${count}`);
    if (ctx.likesReceived >= count) ids.push(`likes_received_${count}`);
    if (ctx.likesGiven >= count) ids.push(`likes_given_${count}`);
  }

  return ids;
}

/* ── Haladás a következő fok felé (GRUNDO #61) ───────────────────── */

/**
 * A létrafok KÜSZÖBE a létra saját mértékegységében: km (táv), m² (terület),
 * nap (sorozat, hűség), hét (heti sorozat), darab (hódítás, komment, szív).
 * Az azonosító végéből jön (`likes_given_50` → 50) — minden létrás azonosító
 * így épül fel. Egyedi jelvénynél `null`.
 */
export function ladderThreshold(def: BadgeDef): number | null {
  if (def.ladder === null) return null;
  const match = /_(\d+)$/.exec(def.id);
  return match ? Number(match[1]) : null;
}

/**
 * Hol tart a felhasználó létránként — ugyanabból a kontextusból, amiből a
 * kiosztás (`earnedBadgeIds`) dolgozik, tehát a kijelzett szám és a kiosztás
 * sosem mondhat ellent egymásnak. Tiszta függvény.
 */
export function ladderProgress(ctx: BadgeContext): Record<BadgeLadder, number> {
  return {
    distance_foot: ctx.distanceKm.run + ctx.distanceKm.walk,
    distance_bike: ctx.distanceKm.ride,
    territory: ctx.territoryM2Total,
    conqueror: ctx.stealCount,
    streak: ctx.streakLongestDays,
    week_streak: Math.max(ctx.weekStreakWeeks ?? 0, ...ctx.weekMilestonesAwarded, 0),
    loyalty: Math.floor(ctx.accountAgeDays),
    explorer: ctx.countriesVisited ?? 0,
    comments: ctx.commentCount,
    likes_received: ctx.likesReceived,
    likes_given: ctx.likesGiven,
  };
}
